import { useState } from 'react';
import Badge from '../shared/Badge';
import Dropdown from '../shared/Dropdown';
import { IconButton } from '../shared/StandardButtons';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';

export default function AIConnectionsModal({ isOpen, onClose, connections, providers, isLoading, onRefresh }) {
  const [expandedConnectionId, setExpandedConnectionId] = useState(null);
  const [editingConnectionId, setEditingConnectionId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedApiKey, setEditedApiKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('');

  if (!isOpen) return null;

  const toggleExpand = (connId) => {
    if (expandedConnectionId === connId) {
      // Collapse
      setExpandedConnectionId(null);
      setEditingConnectionId(null);
    } else {
      // Expand
      setExpandedConnectionId(connId);
      setEditingConnectionId(null);
    }
  };

  const handleEdit = (conn) => {
    setEditingConnectionId(conn.id);
    setEditedName(conn.name || '');
    setEditedApiKey('');
  };

  const handleCancel = () => {
    setEditingConnectionId(null);
    setEditedName('');
    setEditedApiKey('');
  };

  const handleSave = async (conn) => {
    try {
      setIsSaving(true);
      const updates = {
        name: editedName
      };

      // Only include API key if it was changed
      if (editedApiKey.trim()) {
        updates.api_key = editedApiKey;
      }

      await adminApi.modelConnections.update(conn.id, updates);
      toast.success('Connection updated successfully');
      setEditingConnectionId(null);
      setEditedApiKey('');
      await onRefresh();
    } catch (error) {
      console.error('Failed to update connection:', error);
      toast.error('Failed to update connection');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (conn) => {
    try {
      await adminApi.modelConnections.delete(conn.id);
      toast.success('Connection deleted successfully');
      if (expandedConnectionId === conn.id) {
        setExpandedConnectionId(null);
        setEditingConnectionId(null);
      }
      await onRefresh();
    } catch (error) {
      console.error('Failed to delete connection:', error);
      toast.error('Failed to delete connection');
    }
  };

  // Filter connections
  const filteredConnections = connections.filter(conn => {
    const matchesSearch = !searchQuery ||
      conn.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conn.provider?.display_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProvider = !providerFilter || conn.provider_id === providerFilter;
    return matchesSearch && matchesProvider;
  });

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }} onClick={onClose}>
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 border border-gray-700 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">AI Connections</h3>
              <p className="text-gray-400 text-sm mt-1">Manage AI provider connections and API keys</p>
            </div>
            <IconButton onClick={onClose} title="Close">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </IconButton>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search connections..."
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
              />
              <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <Dropdown
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              options={[
                { value: '', label: 'All Providers' },
                ...(providers || []).map(provider => ({
                  value: provider.id,
                  label: provider.display_name
                }))
              ]}
              className="w-48 bg-gray-900 border-gray-700 focus:border-green-500"
            />
          </div>
        </div>

        {/* Connection List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <p className="text-lg font-medium mb-2">No Connections Found</p>
              <p className="text-sm">
                {searchQuery || providerFilter ? 'Try adjusting your filters' : 'No AI provider connections are currently configured'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {filteredConnections.map((conn) => {
                const isExpanded = expandedConnectionId === conn.id;
                const isEditing = editingConnectionId === conn.id;

                return (
                  <div key={conn.id} className="border-b border-gray-700 last:border-b-0">
                    {/* Collapsed Header */}
                    <div
                      onClick={() => toggleExpand(conn.id)}
                      className="flex items-center gap-3 p-4 transition-colors cursor-pointer"
                      style={{
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {/* Chevron */}
                      <div className="text-gray-400">
                        <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium truncate">{conn.name}</h4>
                          <p className="text-sm text-gray-400 truncate">{conn.provider?.display_name}</p>
                        </div>
                        <Badge
                          variant={conn.status === 'active' ? 'active' : conn.status === 'error' ? 'error' : 'inactive'}
                          size="sm"
                        >
                          {conn.status || 'unknown'}
                        </Badge>
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(conn);
                        }}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        title="Delete connection"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 py-4 bg-gray-900/50">
                        <div className="border border-gray-700 rounded-lg p-4 space-y-4">
                          {/* Name Field */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-200 mb-2">
                              Connection Name
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                                placeholder="Enter connection name..."
                              />
                            ) : (
                              <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                                <p className="text-sm text-gray-300">{conn.name || '(No name set)'}</p>
                              </div>
                            )}
                          </div>

                          {/* API Key Field */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-200 mb-2">
                              API Key
                            </label>
                            {isEditing ? (
                              <>
                                <input
                                  type="password"
                                  value={editedApiKey}
                                  onChange={(e) => setEditedApiKey(e.target.value)}
                                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                                  placeholder="Enter new API key (leave blank to keep current)..."
                                />
                                <p className="text-xs text-gray-400 mt-2">
                                  Leave blank to keep the current API key unchanged
                                </p>
                              </>
                            ) : (
                              <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                                <p className="text-sm text-gray-500 font-mono">••••••••••••••••</p>
                              </div>
                            )}
                          </div>

                          {/* Edit Button at Bottom */}
                          <div className="flex justify-end gap-2 pt-2">
                            {isEditing ? (
                              <>
                                <button
                                  onClick={handleCancel}
                                  disabled={isSaving}
                                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-lg text-sm transition-all"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSave(conn)}
                                  disabled={isSaving}
                                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 rounded-lg text-sm transition-all flex items-center gap-2"
                                  style={{ color: 'var(--color-text-on-accent)' }}
                                >
                                  {isSaving ? (
                                    <>
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Save
                                    </>
                                  )}
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleEdit(conn)}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-all flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
